
import { config } from '@/config/env';
import { 
  sanitizeRequestPayload, 
  validateJWTFormat, 
  createSecurePayload, 
  sanitizeErrorMessage,
  logSecurityEvent,
  validateTimestamp 
} from '@/utils/security';
import type { LoginResponse, AuthError } from '@/auth/types';

class AuthApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public code?: string
  ) {
    super(message);
    this.name = 'AuthApiError';
  }
}

const createAuthError = (error: any): AuthError => {
  const sanitizedMessage = sanitizeErrorMessage(error);
  
  if (error instanceof AuthApiError) {
    return {
      message: sanitizedMessage,
      statusCode: error.statusCode,
      code: error.code,
    };
  }

  if (error instanceof Error) {
    return { message: sanitizedMessage };
  }

  return { message: 'An unexpected error occurred' };
};

// Enhanced request validation with security checks
const validateRequest = (payload: any): any => {
  if (!config.security.requestValidationEnabled) {
    return payload;
  }

  // Validate timestamp for CSRF protection
  if (payload.timestamp && !validateTimestamp(payload.timestamp, payload.nonce)) {
    logSecurityEvent('invalid_timestamp', { timestamp: payload.timestamp });
    throw new AuthApiError('Request expired or invalid', 400, 'REQUEST_EXPIRED');
  }

  return sanitizeRequestPayload(payload);
};

// Session storage keys
const AUTH_STORAGE_KEY = 'mayl_auth_session';
const TOKEN_STORAGE_KEY = 'mayl_auth_token';

// Session management utilities
const saveSession = (loginResponse: LoginResponse) => {
  try {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(loginResponse));
    localStorage.setItem(TOKEN_STORAGE_KEY, loginResponse.token);
    logSecurityEvent('session_saved', { userId: loginResponse.user.id });
  } catch (error) {
    logSecurityEvent('session_save_error', { error });
  }
};

const getSession = (): LoginResponse | null => {
  try {
    const sessionData = localStorage.getItem(AUTH_STORAGE_KEY);
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    
    if (!sessionData || !token) return null;
    
    const parsed = JSON.parse(sessionData);
    
    // Validate session structure
    if (!parsed.user || !parsed.user.id || !parsed.user.email) {
      clearSession();
      return null;
    }
    
    return parsed;
  } catch (error) {
    logSecurityEvent('session_retrieve_error', { error });
    clearSession();
    return null;
  }
};

const clearSession = () => {
  try {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    logSecurityEvent('session_cleared');
  } catch (error) {
    logSecurityEvent('session_clear_error', { error });
  }
};

// Mock user data for development - backend will handle real user creation
const createMockUser = (googleProfile: any) => {
  const now = new Date().toISOString();
  return {
    id: `user_${Date.now()}`,
    email: googleProfile.email || 'user@example.com',
    name: googleProfile.name || 'Test User',
    avatar: googleProfile.picture || null,
    createdAt: now,
    lastLoginAt: now,
  };
};

// Decode Google JWT to extract user info (basic decoding for development)
const decodeGoogleJWT = (credential: string) => {
  try {
    const parts = credential.split('.');
    if (parts.length !== 3) throw new Error('Invalid JWT format');
    
    const payload = JSON.parse(atob(parts[1]));
    return {
      email: payload.email,
      name: payload.name,
      picture: payload.picture,
      sub: payload.sub,
    };
  } catch (error) {
    logSecurityEvent('jwt_decode_error', { error });
    throw new AuthApiError('Invalid credential format', 400, 'INVALID_CREDENTIAL');
  }
};

export const authApi = {
  async login(googleCredential: string): Promise<LoginResponse> {
    // Validate JWT format on client side
    if (config.security.jwtValidationEnabled && !validateJWTFormat(googleCredential)) {
      logSecurityEvent('invalid_jwt_format');
      throw new AuthApiError('Invalid credential format', 400, 'INVALID_CREDENTIAL');
    }

    try {
      logSecurityEvent('login_attempt');
      
      // Decode Google JWT to get user info
      const googleProfile = decodeGoogleJWT(googleCredential);
      
      // Create mock user for development
      const user = createMockUser(googleProfile);
      
      const loginResponse: LoginResponse = {
        success: true,
        user,
        token: `mock_token_${user.id}`, // Mock token for development
      };

      // Save session to localStorage
      saveSession(loginResponse);
      
      // Log successful authentication
      logSecurityEvent('login_success', { userId: user.id });

      return loginResponse;
    } catch (error) {
      if (error instanceof AuthApiError) {
        throw error;
      }
      logSecurityEvent('login_network_error', { error });
      throw new AuthApiError('Authentication failed');
    }
  },

  async logout(): Promise<void> {
    try {
      logSecurityEvent('logout_attempt');
      
      // Clear session from localStorage
      clearSession();
      
      logSecurityEvent('logout_success');
    } catch (error) {
      logSecurityEvent('logout_network_error', { error });
      throw new AuthApiError('Logout failed');
    }
  },

  async checkAuth(): Promise<LoginResponse | null> {
    try {
      // Check for existing session in localStorage
      const session = getSession();
      
      if (session) {
        logSecurityEvent('session_restored', { userId: session.user.id });
        return session;
      }
      
      logSecurityEvent('no_active_session');
      return null;
    } catch (error) {
      logSecurityEvent('auth_check_error', { error });
      clearSession();
      return null;
    }
  },
};

export { createAuthError, AuthApiError };
